import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import { Dispatch, SetStateAction } from 'react'
import {
  HiOutlineCheckBadge,
  HiOutlineClock,
  HiOutlineEnvelope,
  HiOutlineGlobeAlt,
  HiOutlineInformationCircle,
  HiOutlinePhone,
  HiOutlineTrash,
} from 'react-icons/hi2'
import { getApiClient } from '../../../../../client'
import { CodeSnippet } from '../../../../../components'
import { Input, Separator, Spinner, Switch, Tabs, Tooltip } from '../../../../../elements'
import type { WebchatFormProps } from './WebchatForm'
import { Callout } from '../../../../../elementsv2'

export function WebchatV1Form({
  workspaceId,
  botId,
  ...props
}: WebchatFormProps & { workspaceId: string; botId: string }) {
  return (
    <Tabs defaultValue="configs">
      <Tabs.List className="justify-center">
        <Tabs.Trigger value="configs">Settings</Tabs.Trigger>
        <Tabs.Trigger value="preconfigured">Pre-configured</Tabs.Trigger>
        <Tabs.Trigger value="configurable">Configurable</Tabs.Trigger>
      </Tabs.List>
      <div className="max-w-2xl px-8 pb-8">
        <Tabs.Content value="configs">
          <WebChatForm {...props} />
        </Tabs.Content>
        <Tabs.Content value="preconfigured">
          <div className="flex grow basis-0 flex-col">
            <h3 className="text-base font-medium text-gray-12">Shareable URL</h3>
            <p className="mb-6 text-xs font-light text-gray-11">
              Share the following link with people that would like to quickly test your chatbot:
            </p>
            <WebChatCode {...{ workspaceId, botId }} codeType="sharableUrl" />
            <Separator className="my-8 w-full" />
            <h3 className="text-base font-medium text-gray-12">Embedded</h3>
            <p className="mb-6 text-xs font-light text-gray-11">This is the recommended way to use the webchat.</p>
            <WebChatCode {...{ workspaceId, botId }} codeType="preconfigured" />
          </div>
        </Tabs.Content>
        <Tabs.Content value="configurable">
          <div className="flex grow basis-0 flex-col">
            <h3 className="mb-2 text-base font-medium text-gray-12">Configurable</h3>
            <Callout color="amber" className="mb-4">
              The configurable webchat code must be updated manually every time there is a change. We highly recommend
              using the Pre-configured version.
            </Callout>
            <WebChatCode {...{ workspaceId, botId }} codeType="configurable" />
          </div>
        </Tabs.Content>
      </div>
    </Tabs>
  )
}

const WebChatCode = ({
  codeType,
  workspaceId,
  botId,
}: {
  codeType: 'preconfigured' | 'configurable' | 'sharableUrl'
  workspaceId: string
  botId: string
}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['webchat', codeType],
    queryFn: () => getApiClient(workspaceId).getBotWebchat({ id: botId, type: codeType }),
  })

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center">
        <Spinner />
      </div>
    )
  }
  //TODO: make this look nicer
  if (isError) {
    return <CodeSnippet code={'Unable to load webchat code'} />
  }
  return <CodeSnippet code={data?.code ?? ''} />
}

const WebChatForm = ({
  data,
  setData,
}: {
  data: Record<string, any>
  setData: Dispatch<SetStateAction<Record<string, any>>>
}) => {
  return (
    <div className="flex flex-col">
      <div className="flex">
        <div className="flex grow basis-0 flex-col">
          <h3 className="text-base font-medium text-gray-12">General</h3>
          <p className="mb-6 text-xs font-light text-gray-11">These will be displayed on the webchat page.</p>
          <div className="flex flex-col gap-8">
            <Input
              label="Bot name"
              placeholder="Amazing Bot"
              value={data.botName ?? ''}
              onChange={(e) => setData((prevData) => ({ ...prevData, botName: e.target.value }))}
            />
            <Input
              label="Avatar URL"
              placeholder="https://example.com/avatar.png"
              value={data.avatarUrl ?? ''}
              onChange={(e) => setData((prevData) => ({ ...prevData, avatarUrl: e.target.value }))}
            />
            <Input
              label="Bot description"
              placeholder="This is an amazing bot"
              value={data.botConversationDescription ?? ''}
              onChange={(e) => setData((prevData) => ({ ...prevData, botConversationDescription: e.target.value }))}
            />
            <Input
              label="Composer placeholder"
              placeholder={'Chat with bot'}
              value={data.composerPlaceholder ?? ''}
              onChange={(e) => setData((prevData) => ({ ...prevData, composerPlaceholder: e.target.value }))}
            />
            <Input
              label={
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://styler.botpress.app"
                  className="group flex items-center gap-2 decoration-gray-12 hover:cursor-pointer hover:text-gray-12 hover:underline"
                >
                  <p>Stylesheet URL</p>
                  <ArrowTopRightOnSquareIcon className="mb-0.5 inline h-4 w-4  group-hover:text-gray-12" />
                </a>
              }
              placeholder="https://example.com/style.css"
              value={data.stylesheet ?? ''}
              onChange={(e) => setData((prevData) => ({ ...prevData, stylesheet: e.target.value }))}
            />
          </div>
        </div>
        <Separator orientation="vertical" className="mx-12" />
        <div className="flex grow basis-0 flex-col">
          <h3 className="text-base font-medium text-gray-12">Bot information</h3>
          <p className="mb-4 text-xs font-light text-gray-11">
            These will be displayed when the user clicks on the bot information page.
          </p>
          <div className="flex flex-col gap-8">
            <Input
              leadingIcon={<HiOutlineGlobeAlt className="mr-1 h-5 w-5 text-gray-11" />}
              placeholder="https://example.com"
              value={data.website ?? ''}
              onChange={(e) => setData((prevData) => ({ ...prevData, website: e.target.value }))}
            />
            <Input
              leadingIcon={<HiOutlinePhone className="mr-1 h-5 w-5 text-gray-11" />}
              placeholder="+1 555 555 5555"
              value={data.phoneNumber ?? ''}
              onChange={(e) => setData((prevData) => ({ ...prevData, phoneNumber: e.target.value }))}
            />
            <Input
              leadingIcon={<HiOutlineEnvelope className="mr-1 h-5 w-5 text-gray-11" />}
              placeholder="example@example.com"
              value={data.emailAddress ?? ''}
              onChange={(e) => setData((prevData) => ({ ...prevData, emailAddress: e.target.value }))}
            />
            <Input
              label="Terms & Conditions"
              placeholder="https://example.com/terms"
              value={data.termsConditions ?? ''}
              onChange={(e) => setData((prevData) => ({ ...prevData, termsConditions: e.target.value }))}
            />
            <Input
              label="Privacy Policy"
              placeholder="https://example.com/privacy"
              value={data.privacyPolicy ?? ''}
              onChange={(e) => setData((prevData) => ({ ...prevData, privacyPolicy: e.target.value }))}
            />
          </div>
        </div>
      </div>
      <Separator orientation="horizontal" className="my-8 w-full" />
      <h3 className="text-base font-medium text-gray-12">Features</h3>
      <p className="mb-4 text-xs font-light text-gray-11">These will add options to the header of the webchat page.</p>
      <div className="grid grid-cols-2 gap-12">
        <Switch
          leadingIcon={<HiOutlineInformationCircle className="mr-1 h-5 w-5 text-gray-11" />}
          label="Show Bot Information"
          className="w-full"
          id="botInfo"
          checked={data.showBotInfoPage}
          onCheckedChange={(checked) => setData((prevData) => ({ ...prevData, showBotInfoPage: checked }))}
        />
        <Switch
          leadingIcon={<HiOutlineCheckBadge className="mr-1 h-5 w-5 text-gray-11" />}
          label='Show "Created with Botpress"'
          className="w-full"
          id="showPoweredBy"
          checked={data.showPoweredBy ?? true}
          onCheckedChange={(checked) => setData((prevData) => ({ ...prevData, showPoweredBy: checked }))}
        />
        <Switch
          leadingIcon={<HiOutlineClock className="mr-1 h-5 w-5 text-gray-11" />}
          label={
            <Tooltip
              label={
                <span className="text-sm">
                  If you enable this option <pre className="inline">useSessionStorage=true</pre> will be added to your
                  webchat configuration, the conversation will be stored in the browser's session storage. This means
                  that the conversation will be cleared when the user closes the browser tab.
                </span>
              }
            >
              <label htmlFor="useSessionStorage" className="text-sm  text-gray-11">
                Clear conversation on exit
              </label>
            </Tooltip>
          }
          className="w-full"
          id="useSessionStorage"
          checked={data.useSessionStorage}
          onCheckedChange={(checked) => setData((prevData) => ({ ...prevData, useSessionStorage: checked }))}
        />
        <Switch
          leadingIcon={<HiOutlineTrash className="mr-1 h-5 w-5 text-gray-11" />}
          label="Allow user to delete conversation"
          className="w-full"
          id="deleteConversation"
          checked={data.enableConversationDeletion}
          onCheckedChange={(checked) => setData((prevData) => ({ ...prevData, enableConversationDeletion: checked }))}
        />
      </div>
    </div>
  )
}
